import { useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { ContactForm } from "../components/ContactForm";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { FaqSection } from "../sections/FaqSection";
import { CATEGORIES } from "../data/categories";
import { useUI } from "../context/UIContext";

export default function CategoryPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace("/", "");
  const cat = CATEGORIES[slug];
  const { openUpload } = useUI();

  if (!cat) return <Navigate to="/" replace />;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: cat.eyebrow,
    provider: { "@type": "ProfessionalService", name: "Energie-Tarifberater" },
    areaServed: "DE",
  };

  return (
    <>
      <Seo
        title={`${cat.eyebrow} vergleichen & wechseln`}
        description={cat.subtitle}
        path={`/${cat.slug}`}
        image={cat.image}
        schema={schema}
      />
      <PageHero
        eyebrow={cat.eyebrow}
        titleLines={cat.titleLines}
        subtitle={cat.subtitle}
        image={cat.image}
        breadcrumb={[{ label: cat.eyebrow }]}
      />

      <section className="bg-background py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12">
          {/* Left: content */}
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-2xl sm:text-3xl font-heading font-semibold text-navy-900 leading-snug mb-12">
                {cat.intro}
              </p>
            </Reveal>

            <div className="grid sm:grid-cols-2 gap-6">
              {cat.benefits.map((b, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="rounded-3xl border border-slate-200 bg-white p-8 h-full hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-35px_rgba(29,78,216,0.25)] transition-[transform,box-shadow] duration-500" data-testid={`benefit-${i}`}>
                    <span className="inline-grid place-items-center h-11 w-11 rounded-xl bg-blue-50 text-blue-700 mb-5">
                      <Check className="h-5 w-5" strokeWidth={2.5} />
                    </span>
                    <h3 className="text-xl font-heading text-navy-900 mb-2">{b.title}</h3>
                    <p className="text-slate-500 leading-relaxed text-sm">{b.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal>
              <div className="mt-10 rounded-3xl bg-blue-700 p-10 flex flex-col sm:flex-row items-center justify-between gap-6">
                <div>
                  <p className="text-5xl font-heading font-semibold text-blue-200">{cat.stat.value}</p>
                  <p className="text-slate-300 mt-2">{cat.stat.label}</p>
                </div>
                <button
                  onClick={openUpload}
                  className="group rounded-full bg-blue-700 hover:bg-blue-600 text-white px-8 py-4 font-medium transition-colors flex items-center gap-2 whitespace-nowrap"
                  data-testid="category-upload-btn"
                >
                  Rechnung prüfen
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            </Reveal>
          </div>

          {/* Right: sticky lead form */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="lg:sticky lg:top-28 rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.15)]"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-blue-700 mb-2">Kostenloser {cat.eyebrow}-Check</p>
              <h3 className="text-2xl font-heading font-semibold text-navy-900 mb-6">
                Angebot in <span className="font-serif italic text-blue-700">24 Stunden</span>
              </h3>
              <ContactForm topic={cat.slug} testid={`category-form-${cat.slug}`} />
            </motion.div>
          </div>
        </div>
      </section>

      <FaqSection />
    </>
  );
}
